import Footer from "@/comps/footer"
import Header from "@/comps/header"
import Meta from "@/comps/meta"
import {
	BlogCategoryPageProps,
	BlogPostShort,
	getServerSidePropsCategory,
} from "@/ssr/blog"
import { assetUrl } from "@/utils/cdn"
import clsx from "clsx"
import { useTranslation } from "next-i18next"
import Link from "next/link"
import { BreadcrumbList, WithContext } from "schema-dts"

const blogJsonLd: WithContext<BreadcrumbList> = {
	"@context": "https://schema.org",
	"@type": "BreadcrumbList",
	itemListElement: [
		{
			"@type": "ListItem",
			position: "0",
			name: "Home",
			item: "https://zoomerang.app/",
		},
		{
			"@type": "ListItem",
			position: "1",
			name: "Tools",
			item: "https://zoomerang.app/blog",
		},
	],
}

export default function BlogPage(
	props: BlogCategoryPageProps,
) {
	const {
		posts,
		pageIndex,
		maxPages,
		current,
		featured,
		categories,
	} = props

	const { t } = useTranslation("common")

	return (
		<div className="flex h-screen flex-col justify-between">
			<div>
				<Meta
					title={t("txt_blog_title")}
					description={t("txt_blog_description")}
					jsonLdData={[blogJsonLd]}
				/>
				<Header />
				<CategoryTabs
					current={current}
					categories={categories}
				/>
				<div
					className={clsx(
						"w-full px-4 tablet:w-[728px] tablet:px-0 desktop:w-[1175px]",
						"mx-auto flex flex-col items-center gap-2 tablet:gap-3 desktop:gap-4",
					)}>
					{featured !== null && (
						<FeaturedPosts posts={featured} />
					)}
					<Posts posts={posts} />
					<PageNumbers
						page={pageIndex}
						max={maxPages}
						current={current}
					/>
				</div>
			</div>
			<Footer />
		</div>
	)
}

interface FeaturedPostsProps {
	posts: BlogPostShort[]
}

function FeaturedPosts(props: FeaturedPostsProps) {
	const { posts } = props

	const [first, second, third] = posts

	return (
		<div className="relative z-20 flex w-full flex-col gap-4 tablet:h-[466px] tablet:flex-row-reverse desktop:h-[542px]">
			<SinglePost post={first} topFeatured />
			<div className="inline-flex shrink-0 flex-col gap-4 tablet:gap-[18px] desktop:gap-[14px]">
				{second && (
					<SinglePost post={second} otherFeatured />
				)}
				{second && (
					<SinglePost post={third} otherFeatured />
				)}
			</div>
		</div>
	)
}

interface CategoryTabsProps {
	current: string
	categories: string[]
}

function CategoryTabs(props: CategoryTabsProps) {
	const { current, categories } = props

	const { t } = useTranslation("page-blog")

	return (
		<div
			className={clsx(
				"relative z-10 flex flex-col items-center",
				"h-[300px] tablet:h-[410px] desktop:h-[576px]",
				"mb-[-150px] tablet:mb-[-170px] desktop:mb-[-320px]",
				"pt-4 tablet:pt-[60px] desktop:pt-[60px]",
				"gap-[22px] tablet:gap-[26px] desktop:gap-6",
			)}>
			<img
				src={assetUrl("/blog/index-background.webp")}
				alt="background cover"
				className={clsx(
					"absolute left-0 top-0 z-10 w-screen object-cover",
					"pointer-events-none",
					"-translate-y-1/3 dark:opacity-40",
				)}
			/>
			<h1 className="text-h1 relative z-20">
				{t("txt_zoomerang_blog")}
			</h1>
			<div
				className={clsx(
					"relative z-20 flex justify-start gap-4 tablet:justify-center",
					"no-scrollbar w-full overflow-x-scroll px-4",
				)}>
				{categories.map((category) => (
					<Link
						key={category}
						href={
							category === "all"
								? "/blog"
								: `/blog/${category}/1`
						}>
						<a
							className={clsx(
								"text-[16px]",
								"rounded-full border",
								"px-[18px] py-[6px]",
								"select-none transition-colors duration-200 ease-in-out",
								current === category
									? [
											"bg-blue-900",
											"text-blue-50",
											"pointer-events-none",
											"font-700",
									  ]
									: [
											"bg-[rgba(255,255,255,0.42)]",
											"border-[rgba(255,255,255,0.62)]",
											"dark:bg-[rgba(0,0,0,0.42)]",
											"dark:border-[rgba(0,0,0,0.42)]",
											"text-blue-900",
											"hover:bg-color-background",
											"font-600",
									  ],
								"shrink-0",
							)}>
							{categoriesMapping.find(
								(mapping) => mapping.value === category,
							)?.name ?? category}
						</a>
					</Link>
				))}
			</div>
		</div>
	)
}

export const categoriesMapping = [
	{ value: "all", name: "All" },

	{ value: "templates", name: "Templates" },
	{ value: "social-media", name: "Social Media" },
	{ value: "app-editor", name: "App Editor" },
	{ value: "trends", name: "Trends" },
	{ value: "tutorials", name: "Tutorials" },
	{ value: "editing-basics", name: "Editing Basics" },
	{ value: "content-ideas", name: "Content Ideas" },
	{ value: "ai-and-tools", name: "AI and Tools" },
	{ value: "e-commerce", name: "E-commerce" },
	{ value: "side-hustle", name: "Side Hustle" },
]

interface PageNumbersProps {
	page: number
	max: number
	current: string
}

function PageNumbers(props: PageNumbersProps) {
	const { page, max, current } = props

	if (max === 1) {
		return <></>
	}

	const pages = getPageNumbers(page, max)

	const hideLeftArrow = page === 1
	const hideRightArrow = page === max

	return (
		<div className="z-20 inline-flex items-center gap-[22px]">
			<ChangePageArrow
				direction="left"
				page={page}
				current={current}
				hide={hideLeftArrow}
			/>
			<div className="flex select-none items-center gap-2 font-600">
				{pages.map((pageIndex, index) => {
					if (pageIndex === null)
						return (
							<span
								key={index}
								className={clsx(
									"h-[34px] w-[34px] rounded-full",
									"pointer-events-none",
									"flex items-center justify-center",
								)}>
								...
							</span>
						)

					const pageAddress =
						current === "all" && pageIndex === 1
							? "/blog"
							: `/blog/${current}/${pageIndex}`

					return (
						<Link key={index} href={pageAddress}>
							<a
								className={clsx(
									page === pageIndex
										? [
												"pointer-events-none bg-primary-500 text-color-white",
										  ]
										: "text-blue-900 hover:bg-blue-200",
									"flex items-center justify-center",
									"h-[34px] w-[34px] rounded-full",
									"transition-colors duration-200 ease-in-out",
								)}>
								{pageIndex}
							</a>
						</Link>
					)
				})}
			</div>
			<ChangePageArrow
				direction="right"
				page={page}
				current={current}
				hide={hideRightArrow}
			/>
		</div>
	)
}

interface ChangePageArrowProps {
	direction: "left" | "right"
	page: number
	current: string
	hide: boolean
}

function ChangePageArrow(props: ChangePageArrowProps) {
	const { page, current, direction, hide } = props

	const pageIndex =
		direction === "left" ? page - 1 : page + 1
	const pageAddress =
		current === "all" && pageIndex === 1
			? "/blog"
			: `/blog/${current}/${pageIndex}`

	if (hide) {
		return (
			<div className="h-[34px] w-[34px] shrink-0"></div>
		)
	}

	return (
		<Link href={pageAddress}>
			<a
				className={clsx(
					"bg-color-cell hover:bg-blue-100",
					"transition-colors duration-200 ease-in-out",
					"border border-color-separator",
					"h-[34px] w-[34px]",
					"rounded-full",
					"flex items-center justify-center",
					"drop-shadow-sm",
				)}>
				<div
					className={clsx(
						"[--icon-color:var(--color-blue-900)]",
						direction === "right" && "rotate-180",
					)}>
					<Arrow />
				</div>
			</a>
		</Link>
	)
}

const Arrow = () => {
	return (
		<svg
			width="5"
			height="8"
			viewBox="0 0 5 8"
			fill="none"
			xmlns="http://www.w3.org/2000/svg">
			<path
				d="M4.00415 7L1 4L4.00415 1"
				stroke="var(--icon-color)"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</svg>
	)
}

function closedInterval(
	start: number,
	end: number,
): number[] {
	return new Array<number>(end - start + 1)
		.fill(0)
		.map((_, i) => start + i)
}

const MAX_PAGE_NUMBERS = 5
function getPageNumbers(
	page: number,
	max: number,
): (number | null)[] {
	if (max < MAX_PAGE_NUMBERS) {
		return closedInterval(1, max)
	}

	if (page === 1) {
		return [1, 2, null, max]
	}

	if (page === 2) {
		return [1, 2, 3, null, max]
	}

	if (page >= max - 2) {
		return [1, null, max - 2, max - 1, max]
	}

	return [1, null, page, page + 1, null, max]
}

interface PostsProps {
	posts: BlogPostShort[]
}

function Posts(props: PostsProps) {
	return (
		<div
			className={clsx(
				"grid w-full grid-cols-2 gap-4 desktop:grid-cols-3 desktop:gap-[25px]",
				"relative z-20",
			)}>
			{props.posts.map((post) => (
				<SinglePost post={post} key={post.slug} />
			))}
		</div>
	)
}

interface SinglePostProps {
	otherFeatured?: boolean
	topFeatured?: boolean
	post: BlogPostShort
}

const blogCdn = process.env.NEXT_PUBLIC_BLOG_CDN_HOST
function SinglePost(props: SinglePostProps) {
	const { post, topFeatured, otherFeatured } = props

	const notFeatured = !topFeatured && !otherFeatured

	return (
		<Link key={post.slug} href={`/blog/${post.slug}`}>
			<a className="group flex flex-1 shrink-0 select-none flex-col gap-2">
				<div
					className={clsx(
						topFeatured && [
							"h-[284px] w-full tablet:h-full",
						],
						otherFeatured && [
							"h-[209px] tablet:h-[164px] tablet:w-[295px]",
							"desktop:h-[200px] desktop:w-[374px]",
						],
						notFeatured && [
							"tablet:w-[356px]",
							"desktop:h-[200px] desktop:w-[374px]",
						],
						"relative overflow-hidden rounded-[12px] transition-all group-hover:rounded-[4px]",
					)}>
					{post.cover && (
						<img
							className={clsx(
								"h-full w-full",
								"group-hover:scale-105",
								"transition-transform duration-200 ease-in-out",
								topFeatured
									? " object-cover object-left-top"
									: "object-cover object-left-top",
							)}
							src={
								post.cover
									? `${blogCdn}/${post.cover}`
									: "/images/placeholder.png"
							}
							alt={post.title}
						/>
					)}
				</div>
				<h3
					className={clsx(
						"font-bold line-clamp-2 shrink-0 font-700 text-blue-700",
						topFeatured && "w-full",
						otherFeatured &&
							"tablet:w-[295px] desktop:w-[374px]",
						topFeatured ? "h-14 text-lg" : "h-12 text-base",
					)}>
					{post.title}
				</h3>
			</a>
		</Link>
	)
}
export const getServerSideProps =
	getServerSidePropsCategory(null, 1)
